/**
 * SegmentedControl — macOS / iOS 세그먼트 컨트롤
 *
 * 용도: 뷰 스위칭 (카드뷰/리스트뷰), 필터 (All/Active/Archived), 모드 전환.
 * macOS Big Sur 이후 스타일: 회색 배경 + 선택된 세그먼트만 흰 배경 + 섀도우.
 *
 * 구현:
 *  - Radio group 시맨틱 (W3C ARIA Authoring Practices)
 *  - Arrow Left/Right 로 세그먼트 이동 (키보드 접근성)
 *  - Selected 세그먼트는 "Lift" 효과 (흰 배경 + shadow)
 *
 * 접근성:
 *  - `role="radiogroup"` + 각 세그먼트 `role="radio"` + `aria-checked`
 *  - `aria-label` 필수 (컨트롤의 목적 설명)
 *  - Focus ring 은 그룹 레벨이 아니라 개별 세그먼트에 표시
 */

import { useCallback, useId, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '../utils/cn';
import { focusRingClasses } from './FocusRing';

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  label: ReactNode;
  /** 선택 불가 세그먼트 */
  disabled?: boolean;
  /** 좌측 아이콘 (optional) */
  icon?: ReactNode;
}

interface SegmentedControlProps<T extends string = string> {
  /** 컨트롤의 목적 설명 — 스크린리더용 (e.g. "View mode") */
  label: string;
  /** 선택지 배열 */
  options: SegmentedControlOption<T>[];
  /** 현재 선택값 */
  value: T;
  /** 변경 콜백 */
  onChange: (value: T) => void;
  /** 크기 */
  size?: 'sm' | 'md';
  /** 추가 className */
  className?: string;
  /** fill 여부 — true 면 부모 전체 폭 채움 */
  fullWidth?: boolean;
}

const SIZE_CLASS = {
  sm: {
    container: 'p-[2px] h-[28px]',
    segment: 'px-2.5 h-[24px] text-[12px]',
  },
  md: {
    container: 'p-[3px] h-[32px]',
    segment: 'px-3 h-[26px] text-[13px]',
  },
} as const;

export function SegmentedControl<T extends string = string>({
  label,
  options,
  value,
  onChange,
  size = 'md',
  className,
  fullWidth = false,
}: SegmentedControlProps<T>) {
  const groupId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, idx: number) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();

      const direction = event.key === 'ArrowRight' ? 1 : -1;
      let nextIdx = idx;
      // 비활성 세그먼트 건너뛰기
      for (let i = 0; i < options.length; i += 1) {
        nextIdx = (nextIdx + direction + options.length) % options.length;
        const candidate = options[nextIdx];
        if (candidate && !candidate.disabled) {
          onChange(candidate.value);
          // focus 이동
          const next = containerRef.current?.querySelectorAll<HTMLButtonElement>(
            '[role="radio"]',
          )[nextIdx];
          next?.focus();
          return;
        }
      }
    },
    [options, onChange],
  );

  const s = SIZE_CLASS[size];

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={label}
      className={cn(
        'inline-flex items-center gap-[2px] rounded-[8px]',
        'bg-[var(--color-admin-surface-2)]',
        'border border-[var(--color-admin-hairline)]',
        s.container,
        fullWidth && 'w-full',
        className,
      )}
    >
      {options.map((opt, idx) => {
        const selected = opt.value === value;
        const disabled = opt.disabled ?? false;
        return (
          <button
            key={`${groupId}-${opt.value}`}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={typeof opt.label === 'string' ? opt.label : undefined}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            onClick={() => {
              if (!disabled) onChange(opt.value);
            }}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            className={cn(
              'inline-flex items-center justify-center gap-1.5',
              'rounded-[6px]',
              'font-medium',
              'transition-all duration-[180ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
              s.segment,
              fullWidth && 'flex-1',
              selected
                ? [
                    'bg-[var(--color-admin-accent-soft)]',
                    'text-[var(--color-admin-accent)]',
                  ]
                : [
                    'text-[var(--color-admin-text-secondary)]',
                    'hover:text-[var(--color-admin-text)]',
                  ],
              disabled && 'opacity-40 cursor-not-allowed',
              focusRingClasses,
            )}
          >
            {opt.icon != null && (
              <span aria-hidden="true" className="inline-flex">
                {opt.icon}
              </span>
            )}
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

SegmentedControl.displayName = 'SegmentedControl';
